import { GrowthBook } from '@growthbook/growthbook-react';

import config from '@/config';

/**
 * The one GrowthBook instance.
 *
 * Lives here rather than in App.tsx because the EPA gate (ENG-5601) reads a
 * flag from a react-router loader, which runs outside React and so cannot use
 * useFeatureIsOn. A second instance would fetch twice and, worse, could
 * disagree with the one the components read.
 */
export const growthbook = new GrowthBook({
  apiHost: 'https://cdn.growthbook.io',
  clientKey: config.growthBookClientKey,
  enableDevMode: true,
  subscribeToChanges: true,
  onFeatureUsage: (featureKey, result) => {
    if (!window.growthbookFeaturesOnUsage) {
      window.growthbookFeaturesOnUsage = {};
    }
    // load feature usage into a global variable to be used in regular js functions
    window.growthbookFeaturesOnUsage[featureKey] = result.value;
  },
});

let loading: Promise<void> | undefined;

/**
 * Start the feature fetch. Idempotent, and never throws: a failed fetch leaves
 * every flag at its default, which for `disable-epa-gate` means the gate runs.
 *
 * Called at the top of main() so the request overlaps the viewer round trip
 * rather than adding to it.
 */
export function loadGrowthBookFeatures(): Promise<void> {
  if (!loading) {
    loading = Promise.resolve(growthbook.loadFeatures()).catch(() => {
      // Drop the memo so a later call can try again. Memoising the rejection
      // would let one bad fetch at boot pin every flag at its default for the
      // rest of the session, the kill switch included, until a full reload.
      // Nothing calls this after boot today, so this changes no behaviour now;
      // it stops the next caller inheriting a helper that quietly gives up
      // after one attempt.
      loading = undefined;
    });
  }
  return loading;
}

/**
 * Resolve when flags are usable, or when `timeoutMs` has elapsed.
 *
 * The timeout is the point. Awaiting the fetch unbounded would let a slow or
 * unreachable GrowthBook CDN hold the whole app at a blank page, which is a
 * worse outage than the one the kill switch exists to end.
 */
export function growthBookReady(timeoutMs = 1500): Promise<void> {
  const started = loadGrowthBookFeatures();
  let timer: ReturnType<typeof setTimeout> | undefined;

  return Promise.race([
    started,
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, timeoutMs);
    }),
  ]).finally(() => clearTimeout(timer));
}

/**
 * Synchronous flag read for code that is not a React component.
 *
 * Returns false before features land. Every caller must be written so that
 * false is the safe answer.
 */
export function isFeatureOn(key: string): boolean {
  return growthbook.isOn(key);
}
